import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import type { ChannelSummary, ProductListItem, ProductListResponse, QueueSettings } from "../lib/types";
import { Card } from "../components/ui/card";
import { SectionHeading } from "../components/ui/section-heading";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";

/** Fallback = mesmo default semeado em catalog.settings (migration 0023),
 * usado so enquanto GET /api/settings/queue nao responde (API de producao
 * ainda sem a onda W0 no ar, 404) ou durante o loading inicial. */
const QUEUE_DEFAULTS: QueueSettings = { queue_age_warn_days: 7, queue_page_size: 8 };

interface QueueItem extends ProductListItem {
  channel_slug: string;
}

/**
 * "Para fazer hoje": a fila de trabalho da equipe, no lugar de memória.
 *
 * Cobre TODOS os canais que o operador tem acesso (plano W3 §5, corrige o
 * comportamento anterior que só olhava `canais[0]`). Cada canal é consultado
 * pelo caminho novo do servidor (`has_photo=false&order=oldest`, W0 §4.1);
 * o sort local abaixo é rede de segurança para quando a API ainda não tem a
 * onda W0 no ar (production, 2026-09-22) — nesse caso ela ignora `order` e
 * devolve na ordem antiga, e o front reordena do mesmo jeito.
 */
export function FilaDeTrabalho({ canais }: { canais: ChannelSummary[] }): JSX.Element | null {
  const slugs = canais.map((c) => c.slug);

  const { data: settings } = useQuery<QueueSettings>({
    queryKey: ["settings-queue"],
    queryFn: async () => {
      const r = await api.get<QueueSettings>("/api/settings/queue");
      return r.data;
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
  const queueAgeWarnDays = settings?.queue_age_warn_days ?? QUEUE_DEFAULTS.queue_age_warn_days;
  const queuePageSize = settings?.queue_page_size ?? QUEUE_DEFAULTS.queue_page_size;

  const results = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: ["fila-de-trabalho", slug, queuePageSize],
      queryFn: async () => {
        const r = await api.get<ProductListResponse>(`/api/channels/${slug}/products`, {
          // sisplan_status=active: inativo no Sisplan nao publica (409) e nao e trabalho de hoje; arquive-o.
          params: { has_photo: "false", order: "oldest", limit: queuePageSize, sisplan_status: "active" },
        });
        return r.data.items.map((item) => ({ ...item, channel_slug: slug }) satisfies QueueItem);
      },
      enabled: slugs.length > 0,
    })),
  });

  if (slugs.length === 0) return null;

  const isLoading = results.some((r) => r.isLoading);
  const merged: QueueItem[] = results.flatMap((r) => r.data ?? []);

  const fila = merged
    .slice()
    .sort((a, b) => {
      const da = a.entry_created_at ? Date.parse(a.entry_created_at) : Number.MAX_SAFE_INTEGER;
      const db = b.entry_created_at ? Date.parse(b.entry_created_at) : Number.MAX_SAFE_INTEGER;
      return da - db;
    })
    .slice(0, queuePageSize);

  const dias = (iso?: string | null): number | null => {
    if (!iso) return null;
    return Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  };

  return (
    <section aria-labelledby="fila-heading" className="gc-12 grid-12">
      {/* Cabecalho FORA do cartao (handoff: ponto + titulo + linha + meta a
       * direita) — antes vinha dentro do Card com acento coral no topo,
       * o que nao e o que o prototipo mede (o cartao "Para fazer hoje" e
       * plano, sem acento; o ponto colorido vive so no cabecalho da secao,
       * igual "Cadastros do catalogo"). */}
      <div className="gc-12">
        <SectionHeading
          id="fila-heading"
          dot="coral"
          title="Para fazer hoje"
          meta={!isLoading ? `${fila.length} sem foto` : undefined}
        />
      </div>
      <div className="gc-12">
        <p className="page-desc" style={{ margin: 0, maxWidth: "52rem" }}>
          Produtos ativos no Sisplan que ainda não têm foto na vitrine, do mais antigo para o mais
          novo, em todos os canais que você acessa. Enquanto não tiver foto, o produto não entra na
          loja.
        </p>
      </div>
      <div className="gc-12">
        <Card flush aria-labelledby="fila-heading">
          {isLoading ? (
            <div className="stack" style={{ padding: "1rem 0" }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : fila.length === 0 ? (
            <p className="text-md muted" style={{ padding: "1rem 0" }}>
              Nada pendente: todo produto ativo já tem foto. 🎉
            </p>
          ) : (
            <div>
              {fila.map((item) => {
                const d = dias(item.entry_created_at);
                return (
                  <Link
                    key={`${item.channel_slug}-${item.sku}`}
                    to={`/canais/${item.channel_slug}/produtos/${item.sku}`}
                    className="row"
                    style={{
                      gridTemplateColumns: slugs.length > 1 ? "1fr auto auto" : "1fr auto",
                      padding: ".375rem 0",
                      minHeight: "2.75rem",
                    }}
                  >
                    <span className="cluster row__cell" style={{ overflow: "visible" }}>
                      <span className="mono truncate" style={{ fontSize: "var(--fs-micro)", color: "var(--color-ink-3)" }}>
                        {item.sku}
                      </span>
                      <span className="truncate" style={{ fontWeight: "var(--fw-bold)" }}>
                        {item.name}
                      </span>
                    </span>
                    {slugs.length > 1 ? (
                      <span className="chip" title="Canal">
                        {item.channel_slug}
                      </span>
                    ) : null}
                    <span className="cluster">
                      {d !== null && (
                        <Badge variant={d >= queueAgeWarnDays ? "warning" : "secondary"}>
                          {d === 0 ? "hoje" : d === 1 ? "há 1 dia" : `há ${d} dias`}
                        </Badge>
                      )}
                      <ArrowRight className="icon" style={{ color: "var(--color-ink-3)" }} aria-hidden />
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
